import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { cn } from "@/lib/utils";
import type { PerpsFundingDailyPoint } from "@/hooks/usePerpsFunding";

interface FundingChartProps {
  data?: PerpsFundingDailyPoint[];
  loading?: boolean;
  className?: string;
}

export function FundingChart({ data = [], loading = false, className }: FundingChartProps) {
  if (loading) {
    return (
      <div className={cn("stat-card h-64", className)}>
        <div className="flex items-center justify-between mb-4">
          <div className="h-5 w-32 bg-muted/50 rounded animate-pulse" />
        </div>
        <div className="h-48 w-full bg-muted/30 rounded animate-pulse" />
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className={cn("stat-card", className)}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Funding (Daily)</h3>
        </div>
        <div className="h-48 flex items-center justify-center text-muted-foreground text-sm">No funding data</div>
      </div>
    );
  }

  const chartData = data.map((d) => ({
    day: new Date(d.day).toLocaleDateString("en-US", { month: "short", day: "numeric" }),
    funding: d.funding,
  }));

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;
    const value = Number(payload[0]?.value ?? 0);
    return (
      <div className="bg-popover border border-border rounded-lg p-3 shadow-lg">
        <p className="text-sm text-muted-foreground mb-1">{label}</p>
        <p className="text-lg font-bold font-mono">{value >= 0 ? "+" : ""}${value.toFixed(2)}</p>
      </div>
    );
  };

  return (
    <div className={cn("stat-card", className)}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">Funding (Daily)</h3>
        <span className="text-sm text-muted-foreground">{data.length} days</span>
      </div>
      <div className="h-48">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
            <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }} />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }}
              tickFormatter={(v) => `$${Number(v).toFixed(0)}`}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="funding" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} opacity={0.8} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
